import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plane, Wallet, Footprints, Train, Car } from "lucide-react";
import { showErrorToast } from "../../application/utils/error-toast";
import type { TransportMode } from "@tabipla/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@tabipla/ui/components/dialog";
import { Button, Input, Badge } from "@tabipla/ui";
import { useCreateTrip } from "../../application/hooks/useTrips";
import { PlaceAutocomplete, type PlaceResult } from "./PlaceAutocomplete";
import { TripDatePicker } from "./TripDatePicker";

const INTERESTS = [
  { value: "sightseeing", label: "観光", emoji: "🏛️" },
  { value: "food", label: "グルメ", emoji: "🍜" },
  { value: "nature", label: "自然", emoji: "🌲" },
  { value: "culture", label: "文化", emoji: "🎭" },
  { value: "shopping", label: "ショッピング", emoji: "🛍️" },
  { value: "adventure", label: "アドベンチャー", emoji: "🎢" },
  { value: "relaxation", label: "リラクゼーション", emoji: "♨️" },
];

const TRANSPORT_MODES: {
  value: TransportMode;
  label: string;
  icon: typeof Footprints;
  description: string;
}[] = [
  { value: "walking", label: "徒歩中心", icon: Footprints, description: "近距離スポット優先" },
  { value: "public_transit", label: "公共交通機関", icon: Train, description: "電車・バス利用" },
  { value: "car", label: "車・レンタカー", icon: Car, description: "駐車場考慮" },
];

interface CreateTripDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId?: string;
}

export function CreateTripDialog({ open, onOpenChange, groupId }: CreateTripDialogProps) {
  const { t } = useTranslation();
  const createMutation = useCreateTrip();

  const [formData, setFormData] = useState({
    title: "",
    destination: "",
    startDate: "",
    endDate: "",
    budget: "",
    interests: [] as string[],
    transportMode: "public_transit" as TransportMode,
  });

  const submitTrip = async () => {
    await createMutation.mutateAsync({
      title: formData.title,
      destination: formData.destination,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      budget: formData.budget ? parseInt(formData.budget, 10) : null,
      interests: formData.interests,
      groupId: groupId,
      transportMode: formData.transportMode,
    });
    toast.success(t("trips.createSuccess", "旅行を作成しました"));
    onOpenChange(false);
    setFormData({
      title: "",
      destination: "",
      startDate: "",
      endDate: "",
      budget: "",
      interests: [],
      transportMode: "public_transit",
    });
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await submitTrip();
    } catch (error) {
      showErrorToast(error, t, { onRetry: submitTrip });
    }
  };

  const toggleInterest = (interest: string) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const handleDestinationChange = useCallback((place: PlaceResult | null) => {
    if (place) {
      // Use the place name or formatted address as destination
      setFormData((prev) => ({
        ...prev,
        destination: place.name || place.formattedAddress,
      }));
    } else {
      setFormData((prev) => ({ ...prev, destination: "" }));
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="flex max-w-lg flex-col gap-0 overflow-hidden p-0 sm:p-0"
      >
        <DialogHeader className="shrink-0 border-b bg-background px-6 pb-4 pt-2 sm:pt-6">
          <DialogTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-primary" />
            {t("trips.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("trips.createDescription", "新しい旅行を計画しましょう")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleCreate} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto overflow-x-hidden px-6 py-4">
            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("trips.tripTitle")}</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t("trips.titlePlaceholder")}
                required
              />
            </div>

            {/* Destination */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("trips.destination")}</label>
              <PlaceAutocomplete
                value={formData.destination}
                onChange={handleDestinationChange}
                placeholder={t("trips.destinationPlaceholder")}
              />
            </div>

            {/* Dates */}
            <TripDatePicker
              startDate={formData.startDate}
              endDate={formData.endDate}
              onStartDateChange={(date) => setFormData({ ...formData, startDate: date })}
              onEndDateChange={(date) => setFormData({ ...formData, endDate: date })}
            />

            {/* Budget */}
            <div className="space-y-2">
              <label className="flex items-center gap-1.5 text-sm font-medium">
                <Wallet className="h-4 w-4 text-muted-foreground" />
                {t("trips.budget")}
                <span className="text-xs text-muted-foreground">
                  ({t("common.optional", "任意")})
                </span>
              </label>
              <Input
                type="number"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                placeholder={t("trips.budgetPlaceholder")}
              />
            </div>

            {/* Transport Mode */}
            <div className="space-y-2">
              <label className="flex items-center gap-1.5 text-sm font-medium">
                <Train className="h-4 w-4 text-muted-foreground" />
                {t("trips.transportMode", "移動手段")}
              </label>
              <div className="flex flex-wrap gap-2">
                {TRANSPORT_MODES.map((mode) => {
                  const Icon = mode.icon;
                  const isSelected = formData.transportMode === mode.value;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, transportMode: mode.value })}
                      className={`flex flex-1 basis-[calc(33.333%-0.5rem)] flex-col items-center gap-1 rounded-lg border p-3 transition-colors active:scale-[0.98] ${
                        isSelected
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border hover:border-primary/50 hover:bg-accent active:bg-primary/10"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-xs font-medium">{mode.label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                {TRANSPORT_MODES.find((m) => m.value === formData.transportMode)?.description}
              </p>
            </div>

            {/* Interests */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("trips.interests")}</label>
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map((interest) => (
                  <Badge
                    key={interest.value}
                    variant={formData.interests.includes(interest.value) ? "default" : "outline"}
                    className="cursor-pointer transition-all hover:scale-105 active:scale-95"
                    onClick={() => toggleInterest(interest.value)}
                  >
                    <span className="mr-1">{interest.emoji}</span>
                    {interest.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Actions - Sticky footer */}
          <div className="sticky bottom-0 flex gap-3 border-t bg-background px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={createMutation.isPending} className="flex-1">
              {createMutation.isPending ? t("trips.creating") : t("trips.create")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
